import React, { FC, useEffect, useRef, useState } from "react";

/** Must match `EMBED_HEIGHT_MESSAGE` in @dreambigger/boa. */
const EMBED_HEIGHT_MESSAGE = "cotribute:portal-embed:height";
/** Must match `EMBED_DATA_CHANGED_MESSAGE` in @dreambigger/boa. */
const EMBED_DATA_CHANGED_MESSAGE = "cotribute:portal-embed:data-changed";

interface BoaEmbedProps {
  /** Path within the boa app, e.g. "/organization/x/applications/1/idv". */
  path: string;
  title: string;
  /** Height before the embedded page reports its own, in px. */
  initialHeight?: number;
  /**
   * Called when the embedded surface changes data this page also shows.
   *
   * Without it the host renders from a store filled once on page load, so a
   * re-request made inside the panel left the surrounding card asserting the
   * previous outcome until a full reload.
   */
  onDataChanged?: () => void;
}

/**
 * Embeds a surface served by the boa app (@dreambigger/boa).
 *
 * That app is reverse-proxied under this origin at `/boa` (dev:
 * setupProxy.js, prod: middleware.ts), which is what lets it reuse the portal's
 * localStorage session — a cross-origin iframe would see nothing. The embedded
 * page reports its content height by postMessage so the frame grows with its
 * content instead of scrolling inside the page.
 *
 * This is the migration seam: as pages move to the monorepo, the CRA app
 * embeds them here until it hands over the whole route.
 */
const BoaEmbed: FC<BoaEmbedProps> = ({
  path,
  title,
  initialHeight = 600,
  onDataChanged,
}) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(initialHeight);
  // Held in a ref so the listener is bound once: re-subscribing whenever the
  // parent re-renders would drop messages that arrive mid-swap.
  const onDataChangedRef = useRef(onDataChanged);
  onDataChangedRef.current = onDataChanged;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Same-origin only: the embed is proxied under this origin, so anything
      // else is not ours.
      if (event.origin !== window.location.origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;

      if (event.data?.type === EMBED_DATA_CHANGED_MESSAGE) {
        onDataChangedRef.current?.();
        return;
      }

      if (event.data?.type !== EMBED_HEIGHT_MESSAGE) return;
      const next = Number(event.data.height);
      if (Number.isFinite(next) && next > 0) {
        setHeight(next);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <iframe
      ref={frameRef}
      title={title}
      src={`/boa${path}`}
      style={{ width: "100%", height, border: "none", display: "block" }}
    />
  );
};

export default BoaEmbed;
